import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { OrganizationMemberRole, User, OrganizationWithUserContext } from "../../types/apiv1";

export enum RejectionReason {
	Network,
	Bug,
	BadCredentials,
	ExternalLogout,
	Logout,
}

export type SessionStateUnauthenticated = {
	authenticated: false,
	//volatile will not persist on a refresh
	volatile: {
		pending:  boolean,
		rejectionReason?: RejectionReason,
	};
};

export type SessionStateAuthenticated = {
	authenticated: true,
	activeOrganization: string,
	user: User,
	organizations: OrganizationWithUserContext[];
};

export type SessionState = SessionStateUnauthenticated | SessionStateAuthenticated;

const initialState = {
	authenticated: false,
	volatile: {
		pending: false,
		rejectionReason: undefined,
	}
} as SessionState;


export const session = createSlice({
	name: 'session',
	initialState,
	reducers: {
		login: (state, action: PayloadAction<{ me: User, organizations: OrganizationWithUserContext[] }>): SessionState => {
			return {
				authenticated: true,
				activeOrganization: action.payload.organizations[0].organization_id,
				user: action.payload.me,
				organizations: action.payload.organizations,
			};
		},
		logout: (state, action: PayloadAction<RejectionReason>): SessionState => {
			return {
				authenticated: false,
				volatile: {
					pending: false,
					rejectionReason: action.payload,
				}
			};
		},
		pending: (state) => {
			if (state.authenticated) throw new Error("cannot start auth when logged in");

			state.volatile.pending = true;
		},
		refreshOrganizations: (state, action: PayloadAction<OrganizationWithUserContext[]>) => {
			if (!state.authenticated) throw new Error("cannot refresh organizations unless logged in");

			state.organizations = action.payload;
			if (state.organizations.find(org => org.organization_id === state.activeOrganization) === undefined) {
				state.activeOrganization = state.organizations[0].organization_id;
			}
		},
		changeOrganization: (state, action: PayloadAction<string>) => {
			if (!state.authenticated) throw new Error("cannot change organization unless logged in");

			if (state.organizations.find(org => org.organization_id === action.payload) !== undefined) {
				state.activeOrganization = action.payload;
			}
		},
	},
});

export function selectRole(state: SessionStateAuthenticated): OrganizationMemberRole {
	const organization = state.organizations
		.find(org => org.organization_id === state.activeOrganization);
	if (!organization) throw Error("invalid activeOrganization");

	return organization.role;
}

export function selectIsAdmin(state: SessionState): state is SessionStateAuthenticated {
	if (!state.authenticated) {
		return false;
	}
	return selectRole(state) >= OrganizationMemberRole.Admin;
}

export function selectIsRoot(state: SessionState): state is SessionStateAuthenticated {
	if (!state.authenticated) {
		return false;
	}
	return selectRole(state) >= OrganizationMemberRole.Root;
}

export const { login, logout, pending, refreshOrganizations, changeOrganization } = session.actions;
export default session.reducer;
